import { LazyImport, LazyModule } from 'owa-bundling';
import { folderStore } from './store/store';
import BaseFolderType from 'owa-service/lib/contract/BaseFolderType';
import Permission from 'owa-service/lib/contract/Permission';

// Import mutators
import './mutators/setMailboxData';
import './mutators/setLoadingStateForMailbox';
import './mutators/setRootFolderDisplayNameForMailbox';
import './mutators/updateFolderTable';
import './mutators/addSharedFolderUserEmail';
import './mutators/loadOutlookFavoritePersonaSearchFoldersMutator';
import './mutators/addFolderToStoreMutators';

// Import orchestrators
import './orchestrators/getPrimaryMailFoldersOrchestrator';
import './orchestrators/initializeArchiveMailboxOrchestrator';
import './orchestrators/initializeSharedFolderRootsOrchestrator';
import './orchestrators/addSharedFolderUserOrchestrator';

// export types
export { default as createNewFolderService } from './services/createNewFolder';
export { default as updateFolder } from './services/updateFolder';
export { default as folderHasChildWithName } from './util/folderHasChildWithName';
export { default as getAllSubFolderIds } from './selectors/getAllSubFolderIds';
export { getFlattenedFoldersList, FlattenedFolder } from './selectors/getFlattenedFoldersList';
export { hasDisplayableChildFolders } from './selectors/hasDisplayableChildFolders';
export { default as Folder, SourceWellKnownFolderType } from './store/schema/Folder';
export { default as FolderStore } from './store/schema/FolderStore';
export { default as MailboxLoadStateEnum } from './store/schema/MailboxLoadStateEnum';
export { default as getFullFolderId } from './util/getFullFolderId';
export {
    default as outlookFavoritePersonaSearchFoldersLoaded,
} from './actions/outlookFavoritePersonaSearchFoldersLoaded';
export {
    resetFolderState,
    folderNameToId,
    getDistinguishedFolder,
    createAndAddFolderToStore,
} from './util/folderStoreUtility';
export {
    deleteFoldersFromFolderTable,
    removeFolderFromParentFolder,
    addFolderInSortedOrderUnderParentFolder,
    moveFolderToParentFolder,
} from './actions/localFolderOperations';
export {
    onCreateNewFolderSuccess,
    getPrimaryMailFolders,
    initializeArchiveMailboxFromSessionData,
    initializeSharedFolderRoots,
    addSharedFolderUser,
    onSharedFolderFetchFailed,
    getFolderPermissionsSuccess,
    getFolderPermissionsFailed,
    updateFolderPermissionsSuccess,
    updateFolderPermissionsFailed,
    onCreateNewFolderInboxRulesResponse,
} from './actions/publicActions';
export { default as renameFolderService } from './services/renameFolder';
export { default as getDefaultRequestOptions } from './services/getDefaultRequestOptions';
export {
    PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID,
    ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID,
    PRIMARY_DELETED_ITEMS_DISTINGUISHED_ID,
    ARCHIVE_DELETED_ITEMS_DISTINGUISHED_ID,
    PRIMARY_DUMPSTER_DISTINGUISHED_ID,
    ARCHIVE_DUMPSTER_DISTINGUISHED_ID,
    SHARED_FOLDERS_TREE_TYPE,
    PRIMARY_FOLDERS_TREE_TYPE,
    ARCHIVE_FOLDERS_TREE_TYPE,
    FAVORITE_FOLDERS_TREE_TYPE,
} from './util/folderConstants';
export { default as getArchiveMailboxRootFolder } from './selectors/getArchiveMailboxRootFolder';
export { default as getPrimaryMailboxRootFolder } from './selectors/getPrimaryMailboxRootFolder';
export { default as getFolderTable } from './selectors/getFolderTable';
export { default as getMailboxLoadingState } from './selectors/getMailboxLoadingState';
export { default as getSharedFolderUserEmails } from './selectors/getSharedFolderUserEmails';
export { default as getSharedFolderRoot } from './selectors/getSharedFolderRoot';
export {
    default as getEffectiveFolderDisplayName,
} from './selectors/getEffectiveFolderDisplayName';

export { default as isFolderUnderMsg, isFolderOrSubFolderOfMsgRoot } from './util/isFolderUnderMsg';
export {
    FolderTableState,
    isDistinguishedFolder,
    isSubFolderOfDistinguishedFolder,
    isUnderDistinguishedFolder,
    default as isFolderOrSubFolderOfDistinguishedFolder,
} from './util/isFolderOrSubFolderOfDistinguishedFolder';
export {
    shouldDisplayFolderInFolderTree,
    getFolderByDistinguishedId,
    getMailRootFolderChildIds,
    getfolderChildIdsThatCanBeDisplayed,
    isFolderOfTreeType,
    getTreeTypeFromFolderId,
    GEEK_FOLDERS,
    getFoldersToShowFirst,
} from './util/folderUtility';
export {
    getAnchorMailboxHeaderForFolderId,
    getAnchorMailboxHeaderForItemId,
} from './util/mailboxUtility';
export { isPublicFolder } from './util/idConverter';
export { default as getAllSharedFoldersData } from './selectors/getAllSharedFoldersData';

export default folderStore;

// Required for actions that depend on them.
export { BaseFolderType, Permission };

const lazyModule = new LazyModule(() =>
    import(/* webpackChunkName: "ArchiveAndSharedFolders" */ './lazyIndex')
);

// Export lazy loaded actions
export const lazyGetArchiveFolders = new LazyImport(lazyModule, m => m.getArchiveFolders);
export const lazyGetSharedFoldersForSpecificRoot = new LazyImport(
    lazyModule,
    m => m.getSharedFolders
);
export const lazyRemoveSharedFolderUser = new LazyImport(lazyModule, m => m.removeSharedFolderUser);

export const lazyGetFolderPermissions = new LazyImport(lazyModule, m => m.getFolderPermissions);

export const lazyUpdateFolderPermissions = new LazyImport(
    lazyModule,
    m => m.updateFolderPermissions
);

export const lazyUpdateFolderPermissionsInFolderStore = new LazyImport(
    lazyModule,
    m => m.updateFolderPermissionsInStore
);

// Export utils
export const lazyArePermissionArraysEqual = new LazyImport(
    lazyModule,
    m => m.arePermissionArraysEqual
);

export const lazyArePermissionsEqual = new LazyImport(lazyModule, m => m.arePermissionsEqual);
